import { useState } from 'react';
import { PerformanceTab, PerformanceDateRange } from '@/modules/sales-agent/performance/performance-tab';
import { RecommendationTab } from '@/modules/sales-recommendations/recommendation-tab';
import { useSearchParams } from 'react-router';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { CreateNudgeButton } from '@/modules/sales-agent/components/create-nudge-button';
import { NudgesTab } from '@/modules/sales-agent/components/nudges-tab';
import type { NudgePanelState } from '@/modules/sales-agent/components/nudge-panel';
import { SettingsTab } from '@/modules/sales-agent/settings/components/settings-tab';
import { DiscountLimitsTab } from '@/modules/sales-agent/discount/components/discount-limits-tab';

// Each tab keeps its existing URL; Performance is the Sales Agent landing view.
const TABS = [
	{ value: 'performance', label: 'Performance' },
	{ value: 'recommendation', label: 'Recommendation' },
	{ value: 'discount-limits', label: 'Discount Limits' },
	{ value: 'nudges', label: 'Nudges' },
	{ value: 'settings', label: 'Settings' }
] as const;
type SalesAgentTab = (typeof TABS)[number]['value'];

const DEFAULT_TAB: SalesAgentTab = 'performance';

// Same underline tabs as the Integrations and Operating Hours headers.
const TAB_TRIGGER = cn(
	'h-10 flex-none gap-1.5 px-0 py-[11px] text-[13px] font-normal leading-[18px] bg-transparent rounded-none border-0 shadow-none text-gray-800 transition-colors',
	'data-[state=active]:text-ink-strong data-[state=active]:font-medium data-[state=active]:border-b-2 data-[state=active]:border-ink-strong data-[state=active]:drop-shadow-none'
);

const isSalesAgentTab = (value: string | null): value is SalesAgentTab => TABS.some((tab) => tab.value === value);

/**
 * The Sales Agent page (Figma Kim-AI 6825:13106): the header and its tabs, each tab its own
 * feature.
 */
export default function SalesAgent() {
	// In the URL so a link can land on a tab and Back returns to it.
	const [params, setParams] = useSearchParams();
	const requestedTab = params.get('tab');
	const tab: SalesAgentTab = isSalesAgentTab(requestedTab) ? requestedTab : DEFAULT_TAB;
	const setTab = (next: string) =>
		setParams(
			(prev) => {
				if (next === DEFAULT_TAB) prev.delete('tab');
				else prev.set('tab', next);
				return prev;
			},
			{ replace: true }
		);
	const [panel, setPanel] = useState<NudgePanelState | null>(null);

	const catalogue = params.get('catalogue');
	if (tab === 'recommendation' && ['productIds', 'tags', 'collectionIds'].includes(catalogue ?? '')) {
		return <RecommendationTab />;
	}

	return (
		<Tabs value={tab} onValueChange={setTab} className='flex h-full flex-col gap-0'>
			<div className='flex h-[60px] shrink-0 items-center justify-between px-4'>
				<h1 className='text-base font-semibold text-ink'>Sales Agent</h1>
				{tab === 'performance' && <PerformanceDateRange />}
				{tab === 'nudges' && <CreateNudgeButton onCreate={setPanel} />}
			</div>

			<div className='shrink-0 border-b border-gray-300 px-3'>
				<TabsList className='flex h-auto justify-start gap-4 rounded-none border-0 bg-transparent p-0'>
					{TABS.map((item) => (
						<TabsTrigger key={item.value} value={item.value} className={TAB_TRIGGER}>
							{item.label}
						</TabsTrigger>
					))}
				</TabsList>
			</div>

			<TabsContent value='performance' className='min-h-0 flex-1 overflow-y-auto'><PerformanceTab /></TabsContent>
			<TabsContent value='recommendation' className='flex min-h-0 flex-1 flex-col overflow-auto'>
				<RecommendationTab />
			</TabsContent>
			<TabsContent value='discount-limits' className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
				<DiscountLimitsTab />
			</TabsContent>
			<TabsContent value='nudges' className='flex min-h-0 flex-1 flex-col'>
				<NudgesTab panel={panel} onPanelChange={setPanel} />
			</TabsContent>
			<TabsContent value='settings' className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
				<SettingsTab />
			</TabsContent>
		</Tabs>
	);
}
